import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { LOGO_URL } from "@/lib/brand";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Navbar() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const links = [
    { href: "#services", label: t("nav.services") },
    { href: "#about", label: t("nav.about") },
    { href: "#team", label: t("nav.team") },
    { href: "#why", label: t("nav.why") },
    { href: "#karriere", label: t("nav.careers") },
    { href: "#insights", label: t("nav.news") },
    { href: "#contact", label: t("nav.contact") },
  ];

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#0A192F]/95 backdrop-blur-md border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto pl-2 pr-4 sm:pl-3 sm:pr-6 lg:pl-4 lg:pr-8 flex items-center justify-between h-24 sm:h-36 lg:h-24">
        <Link to="/" data-testid="navbar-logo" className="flex items-center">
          <img
            src={LOGO_URL}
            alt="X10 Management · Transforming the Mittelstand"
            className="w-auto h-20 sm:h-32 lg:h-20"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              className="text-sm font-medium link-underline transition-colors text-slate-200 hover:text-white"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <LanguageSwitcher variant="light" />
          <a
            href="#contact"
            data-testid="navbar-cta"
            className="inline-flex items-center px-5 py-2.5 text-sm font-semibold rounded-full transition-all duration-300 bg-white text-[#0A192F] hover:bg-[#C9A96E] hover:text-[#0A192F]"
          >
            {t("nav.cta")}
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 text-white"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#0A192F]/98 backdrop-blur-md border-t border-white/10">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-base font-medium text-white"
                data-testid={`mobile-nav-${l.href.slice(1)}`}
              >
                {l.label}
              </a>
            ))}
            <div className="pt-4 mt-2 border-t border-white/10 flex items-center justify-between">
              <LanguageSwitcher variant="light" />
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                className="px-5 py-3 bg-white text-[#0A192F] rounded-full text-center font-semibold hover:bg-[#C9A96E] transition-colors"
              >
                {t("nav.cta")}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
